import {Injectable} from '@angular/core';
import {BaseService} from './base-service';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {Meterdata, MeterDataHistoryPostResponse, MeterdataType, MeterIdStreamId, SimpleMeterdataHistory, TimeSeriesData} from './meter-provider.interface';
import {catchError, map} from 'rxjs/operators';
import moment, {Duration, Moment, unitOfTime} from 'moment-timezone';
import {ClassTransformer, Transform, Type} from 'class-transformer';
import {AppConstants} from "@shared/util/app-constants";
import {CountryAndTimezoneService} from '../country-timezone/country-and-timezone.service';
import {DataInterval, MeterConsumptionType} from '@app/layout/sitemanager/meters/meters.service';
import {CollectionUtils} from "@shared/util/collections/collection-utils";
import {GRAPHQL_TYPES, HttpUtilityService} from "@shared/services/api/http-utility-service";
import {MeterProvider, MeterUtilityType} from "@app/graphql-typings/graphql";
import {PagedModel} from "@shared/api/model/paged-model";
import * as gql from "gql-query-builder";
import {MeterUtilService} from "@app/layout/meters/meter-util.service";
import {SiteId} from "@shared/services/api/access-sites.interface";
import {DurationTuple} from "@shared/api/model/timestamp";
import {SiteService} from './site.service';


export enum AggregateEntity {
  STREAM = 'stream',
  METER = 'meter',
  SITE = 'site',
}

export enum MeterDataResultType {
  DATA = 'data',
  COST = 'cost',
  EMISSIONS = 'emissions',
  NORMALISATION = 'normalisation',
}


export const AGGREGATE_PERIOD_ALL: 'all' = 'all';

export type AggregatePeriod = DataInterval | typeof AGGREGATE_PERIOD_ALL;

export interface MeterDataSearchCriteria {
  siteId?: SiteId;
  siteIds?: SiteId[];
  meterIdsStreamIds?: MeterIdStreamId[];
  nmiIds?: number[];
  startTs?: Moment;
  endTs?: Moment;
  localStartTs?: Moment;
  localEndTs?: Moment;
  latest?: boolean;
  dataType?: MeterdataType;
  respectSiteUnits?: boolean;
  aggregatePeriod?: AggregatePeriod;
  aggregateEntity?: AggregateEntity;
  resultTypes?: MeterDataResultType[];
  utilityTypes?: MeterUtilityType[],
  preferRollup?: boolean;
}

interface MeterProvidersSearchTerm {
  provider_id?: number;
  provider_ids?: number[];
}


class TimeSeriesDataImpl implements TimeSeriesData {
  data?: number;
  cost?: number;
  @Type(() => moment)
  @Transform(({value}) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}) => value ? value.toJSON() : value, {toPlainOnly: true})
  ts: Moment;
  update?: boolean;
}

class SimpleMeterdataHistoryImpl implements SimpleMeterdataHistory {
  nmi_id: number;
  stream_id: string;
  site_id: number;
  @Type(() => TimeSeriesDataImpl)
  data: TimeSeriesData[];
  data_type?: MeterdataType;
}


export interface MeterStreamHistoryRange extends MeterIdStreamId {
  start_ts: Moment;
  end_ts: Moment;
}

export interface MeterDataRefreshOptions {
  fill_ts_range?: boolean;
  start_ts?: Moment;
  end_ts?: Moment;
}


class MeterStreamHistoryRangeImpl implements MeterStreamHistoryRange {
  nmi_id: number;
  stream_id: string;

  @Type(() => moment)
  @Transform(({value}) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}) => value ? value.toJSON() : value, {toPlainOnly: true})
  end_ts: Moment;

  @Type(() => moment)
  @Transform(({value}) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}) => value ? value.toJSON() : value, {toPlainOnly: true})
  start_ts: Moment;
}


export interface MeterDataRefreshResult {
  submitted: boolean;
}


interface EstimationWindow {
  start_ts: string;
  end_ts: string;
}


export interface EstimatedMeterData {
  nmi_id: number;
  stream_id: string;
  data_type: MeterdataType;
  start_ts: Moment;
  end_ts: Moment;
  data_interval: string;
  count: number;
}


class EstimatedMeterDataImpl implements EstimatedMeterData {
  nmi_id: number;
  stream_id: string;
  data_type: MeterdataType;
  data_interval: string;
  count: number;

  @Type(() => moment)
  @Transform(({value}) => value ? moment.utc(value) : value, {toClassOnly: true})
  @Transform(({value}) => value ? value.toJSON() : value, {toPlainOnly: true})
  start_ts: moment.Moment;

  @Type(() => moment)
  @Transform(({value}) => value ? moment.utc(value) : value, {toClassOnly: true})
  @Transform(({value}) => value ? value.toJSON() : value, {toPlainOnly: true})
  end_ts: moment.Moment;
}


export interface MeterDataEstimationResult {
  detectedDataPeriod?: string;
  gaps?: EstimatedMeterData[];
  error?: string;
}


enum MeterDataRollupType {
  DAILY, MONTHLY
}


@Injectable({
  providedIn: 'root'
})
export class MeterProviderService extends BaseService {

  private readonly logger: AceLogger;
  private readonly PAGE_SIZE = 500;

  private readonly NMI_ID_STREAM_ID_SEP = '-';
  // 8 years in milliseconds
  private readonly MAX_MS_PER_STREAM_TO_FETCH = 8 * 365 * 24 * 60 * 60 * 1000;
  // assume default meter data interval to be 15 mins
  private readonly DALIY_ROLLUP_MULTIPLY_FACTOR = 4 * 24;
  // assume 30 days in a month
  private readonly MONTHLY_ROLLUP_MULTIPLY_FACTOR = this.DALIY_ROLLUP_MULTIPLY_FACTOR * 30;


  private readonly METER_DATA_TYPES_WITH_ROLLUP = new Set<MeterdataType>([MeterdataType.ACTIVE_NET, MeterdataType.REACTIVE_NET]);
  private readonly ROLLUP_TYPES_BY_AGGREGATE_PERIOD = new Map<AggregatePeriod, MeterDataRollupType[]>([
    [DataInterval.P1D, [MeterDataRollupType.DAILY]],
    [DataInterval.P1W, [MeterDataRollupType.DAILY]],
    [DataInterval.P1M, [MeterDataRollupType.MONTHLY, MeterDataRollupType.DAILY]],
    [AGGREGATE_PERIOD_ALL, [MeterDataRollupType.MONTHLY, MeterDataRollupType.DAILY]]
  ]);
  // Note: default timezones by country should be ordered by name of country alphabetically
  private defaultTimezoneByCountry: Map<string, string> = new Map([
    [this.countryAndTimezoneService.COUNTRY_AUSTRALIA, this.countryAndTimezoneService.AUSTRALIA_BRISBANE],
    [this.countryAndTimezoneService.COUNTRY_AUSTRIA, this.countryAndTimezoneService.AUSTRIA_VIENNA],
    [this.countryAndTimezoneService.COUNTRY_BRAZIL, this.countryAndTimezoneService.BRAZIL_EAST],
    [this.countryAndTimezoneService.COUNTRY_CANADA, this.countryAndTimezoneService.CANADA_MOUNTAIN],
    [this.countryAndTimezoneService.COUNTRY_CHINA, this.countryAndTimezoneService.ASIA_SHANGHAI],
    [this.countryAndTimezoneService.COUNTRY_FINLAND, this.countryAndTimezoneService.EUROPE_HELSINKI],
    [this.countryAndTimezoneService.COUNTRY_INDONESIA, this.countryAndTimezoneService.ASIA_JAKARTA],
    [this.countryAndTimezoneService.COUNTRY_IRELAND, this.countryAndTimezoneService.EUROPE_DUBLIN],
    [this.countryAndTimezoneService.COUNTRY_LITHUANIA, this.countryAndTimezoneService.EUROPE_VILNIUS],
    [this.countryAndTimezoneService.COUNTRY_NEW_ZEALAND, this.countryAndTimezoneService.PACIFIC_AUCKLAND],
    [this.countryAndTimezoneService.COUNTRY_SINGAPORE, this.countryAndTimezoneService.ASIA_SINGAPORE],
    [this.countryAndTimezoneService.COUNTRY_SWEDEN, this.countryAndTimezoneService.EUROPE_STOCKHOLM],
    [this.countryAndTimezoneService.COUNTRY_SWITZERLAND, this.countryAndTimezoneService.EUROPE_ZURICH],
    [this.countryAndTimezoneService.COUNTRY_UK, this.countryAndTimezoneService.EUROPE_LONDON],
    [this.countryAndTimezoneService.COUNTRY_USA, this.countryAndTimezoneService.AMERICA_CHICAGO]
  ]);

  constructor(private http: HttpClient,
              private classTransformer: ClassTransformer,
              private countryAndTimezoneService: CountryAndTimezoneService,
              private httpUtilityService: HttpUtilityService,
              private meterUtilService: MeterUtilService,
              private siteService: SiteService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(MeterProviderService.name);
  }

  private verifyGetMeterDataSearchCriteria(searchCriteria: MeterDataSearchCriteria) {
    if (!searchCriteria.latest &&
        ((searchCriteria.startTs == null || searchCriteria.endTs == null) && (searchCriteria.localStartTs == null || searchCriteria.localEndTs == null))) {
      throw new Error('Cannot get non latest meter data without startTs or endTs.');
    }
    if (searchCriteria.siteId == null && !searchCriteria.siteIds?.length && !searchCriteria.nmiIds?.length && !searchCriteria.meterIdsStreamIds?.length) {
      throw new Error('Cannot get meter data without siteId, siteIds, nmiIds or meterIdsStreamIds');
    }
  }

  public getMeterData(searchCriteria: MeterDataSearchCriteria, dataInterval?: Duration): Observable<SimpleMeterdataHistory[]> {
    this.verifyGetMeterDataSearchCriteria(searchCriteria);
    const params = {
      data_type: searchCriteria.dataType || MeterdataType.ACTIVE_NET,
      ...searchCriteria.meterIdsStreamIds?.length && {
        nmi_ids_stream_ids: searchCriteria.meterIdsStreamIds.map(({ nmi_id, stream_id }) => nmi_id + '-' + stream_id)
      },
      ...searchCriteria.siteId != null && {site_id: searchCriteria.siteId},
      ...searchCriteria.siteIds?.length && {siteIds: searchCriteria.siteIds},
      ...searchCriteria.nmiIds?.length && {
        nmi_ids: searchCriteria.nmiIds
      },
      ...searchCriteria.startTs && { start_ts: searchCriteria.startTs.clone().utc().format() },
      ...searchCriteria.endTs && { end_ts: searchCriteria.endTs.clone().utc().format() },
      ...searchCriteria.localStartTs && { local_start_ts: searchCriteria.localStartTs.format(AppConstants.TIMESTAMP_FORMAT) },
      ...searchCriteria.localEndTs && { local_end_ts: searchCriteria.localEndTs.format(AppConstants.TIMESTAMP_FORMAT) },
      ...searchCriteria?.aggregatePeriod && {
        aggregate_period: (searchCriteria.aggregatePeriod === AGGREGATE_PERIOD_ALL ? AGGREGATE_PERIOD_ALL : DataInterval.toIsoString(searchCriteria.aggregatePeriod))
      },
      ...searchCriteria?.aggregateEntity && {aggregate_entity: searchCriteria?.aggregateEntity},
      ...searchCriteria?.respectSiteUnits != null && {respect_site_units: searchCriteria.respectSiteUnits.toString()},
      ...searchCriteria?.resultTypes?.length && {result_types: searchCriteria.resultTypes},
      ...searchCriteria?.utilityTypes?.length && {utility_types: searchCriteria.utilityTypes},
      ...searchCriteria?.preferRollup != null && {prefer_rollup: searchCriteria.preferRollup}
    };
    // if only one row is presented, adjust the endTs to next interval
    // because history look up is start_ts <= ts < end_ts
    if (dataInterval && searchCriteria.startTs?.isSame(searchCriteria.endTs)) {
      params['end_ts'] = searchCriteria.endTs.clone().add(dataInterval).utc().format();
    }

    const options = { headers: this.getHeaders(), params };

    return this.http.get<SimpleMeterdataHistory[]>(
        this.getGateway() + '/meterdata/history', options)
        .pipe(
          map(response => {
            if (response['data'] && response['data']['meterdata_history']) {
              return this.classTransformer.plainToInstance(SimpleMeterdataHistoryImpl, response['data']['meterdata_history']);
            } else {
              throw new Error('Invalid load meterdata response: ' + JSON.stringify(response));
            }
          })
        );
  }


  public getAllMeterProviders(meterProvidersSearchTerm: MeterProvidersSearchTerm = {}): Observable<MeterProvider[]> {
    const searchPageWrapper = (pageInfo): Observable<PagedModel<MeterProvider[]>> => this.getMeterProviders(meterProvidersSearchTerm, pageInfo);
    return this.httpUtilityService.pageUntilComplete(searchPageWrapper, {first: 0, rows: this.PAGE_SIZE});
  }

  private getMeterProviders(meterProvidersSearchTerm: MeterProvidersSearchTerm, pageInfo: {first: number, rows: number}): Observable<PagedModel<MeterProvider[]>> {
    const providerQuery = {
      operation: 'meter_providers',
      fields: [
        'collector_email_address',
        'contact_email_address',
        'provider_id',
        'provider_name',
        'validate_nmi_numbers'
      ],
      variables: {
        provider_id: {type: GRAPHQL_TYPES.INTEGER, required: false},
        provider_ids: {type: GRAPHQL_TYPES.INTEGER, list: true, required: false},
        start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
        limit: {type: GRAPHQL_TYPES.INTEGER, required: true}
      }
    };
    const query = gql.query(providerQuery);
    const variables = {...meterProvidersSearchTerm, ... {start_index: pageInfo.first, limit: pageInfo.rows}};
    return this.httpUtilityService.graphQL<MeterProvider[]>(query, providerQuery.operation, variables);
  }

  public uploadMeterData(meterdata: Meterdata[]): Observable<MeterDataHistoryPostResponse> {
    const options = { headers: this.getHeaders() };
    return this.http.post<MeterDataHistoryPostResponse>(this.getGateway() + '/meterdata/history', meterdata, options);
  }

  getDefaultTimezone(country: string): string {
    return this.defaultTimezoneByCountry.get(country);
  }

  isDefaultTimezone(timezone: string, country: string) {
    return timezone === this.getDefaultTimezone(country);
  }

  getGlobalDefaultTimezone(): string {
    return this.countryAndTimezoneService.AUSTRALIA_BRISBANE;
  }

  toMeterResultType(consumptionType: MeterConsumptionType): MeterDataResultType {
    switch (consumptionType) {
      case MeterConsumptionType.USAGE:
      case MeterConsumptionType.DEMAND:
        return MeterDataResultType.DATA;
      case MeterConsumptionType.NORMALISATION:
        return MeterDataResultType.NORMALISATION;
      case MeterConsumptionType.COST:
        return MeterDataResultType.COST;
      case MeterConsumptionType.EMISSIONS:
        return MeterDataResultType.EMISSIONS;
      default:
        throw new Error('Unknown consumption type ' + consumptionType);
    }
  }

  toMeterResultTypes(consumptionTypes: MeterConsumptionType[]): MeterDataResultType[] {
    return (consumptionTypes || []).map(consumptionType => this.toMeterResultType(consumptionType));
  }

  private getMeterDataHistoryRange(meterDataHistoryPath: string, meterIdsStreamIds: MeterIdStreamId[]): Observable<MeterStreamHistoryRange[]> {
    const options = {
      headers: this.getHeaders(),
      params: {
        nmi_ids_stream_ids: meterIdsStreamIds.map(meterIdStreamId =>
          meterIdStreamId.nmi_id + this.NMI_ID_STREAM_ID_SEP + meterIdStreamId.stream_id)
      }
    };
    return this.http.get(this.getGateway() + meterDataHistoryPath, options).pipe(map(response =>
      this.classTransformer.plainToInstance(
        MeterStreamHistoryRangeImpl,
        this.httpUtilityService.extractResponseData<MeterStreamHistoryRange[]>(response, 'history_ranges'))));
  }

  public getMeterDataSourceHistoryRangeForMeterStream(meterIdStreamId: MeterIdStreamId): Observable<MeterStreamHistoryRange> {
    return this.getMeterDataHistoryRange('/meters/meterdata/source/ranges', [meterIdStreamId])
      .pipe(map(ranges => CollectionUtils.only(ranges)));
  }

  public getMeterDataHistoryRangeForMeterStream(meterIdStreamId: MeterIdStreamId): Observable<MeterStreamHistoryRange> {
    return this.getMeterDataHistoryRange('/meters/meterdata/ranges', [meterIdStreamId])
      .pipe(map(ranges => CollectionUtils.only(ranges)));
  }

  public recalculatingMeterStreamData(nmiId: number, streamId: string, refreshOptions: MeterDataRefreshOptions): Observable<MeterDataRefreshResult> {
    const params = {
      nmi_ids_stream_ids: [this.meterUtilService.buildUniqueStreamId(nmiId, streamId)],
      ...refreshOptions
    };
    return this.http.post(this.getGateway() + '/meters/meterdata/refresh', params).pipe(map((response) =>
      this.httpUtilityService.extractResponseData(response, 'meterdata_refresh')));
  }

  public estimateMeterData(meterIdStreamIds: MeterIdStreamId[], estimationWindows: EstimationWindow[], forcedDataPeriod: string | null): Observable<MeterDataEstimationResult> {
    return this.http.post(this.getGateway() + '/meters/meterdata/estimation', {
      nmi_ids_stream_ids: meterIdStreamIds.map(meterIdStreamId =>
        this.meterUtilService.buildUniqueStreamId(meterIdStreamId.nmi_id,  meterIdStreamId.stream_id)),
      estimation_windows: estimationWindows,
      ...forcedDataPeriod && {forced_data_period: forcedDataPeriod}
    }).pipe(
      map(response => (<MeterDataEstimationResult>{
        gaps: this.classTransformer.plainToInstance(
          EstimatedMeterDataImpl,
          this.httpUtilityService.extractResponseData<EstimatedMeterData[]>(response, 'estimated_gaps'))
      })),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 409 && error.error?.data?.detected_data_period) {
          return of(<MeterDataEstimationResult>{
            detectedDataPeriod: error.error.data.detected_data_period
          });
        } else if (error.status === 400 && error.error?.message) {
          return of(<MeterDataEstimationResult>{error: error.error.message});
        } else {
          return throwError(() => error);
        }
      }));
  }

  private calculateMeterDataFetchChunkSize(streamCount: number, dataInterval: DurationTuple): number {
    const intervalUnit = dataInterval.unit;
    const chunkSize = Math.floor(this.MAX_MS_PER_STREAM_TO_FETCH / (streamCount * moment.duration(dataInterval.amount, intervalUnit).asMilliseconds()));
    if (chunkSize >= 1) {
      return chunkSize;
    } else {
      this.logger.error('Meter data fetch chunk size is shorter than 1 ' + intervalUnit + ', defaulting to 1 ' + intervalUnit + '.');
      return 1;
    }
  }

  private isLocalTsAlignedWithBoundary(localTs: Moment, unit: unitOfTime.Base): boolean {
    const flooredLocalTs = localTs.clone().startOf(unit);
    return localTs.isSame(flooredLocalTs);
  }

  private isTsAlignedWithBoundary(ts: Moment, timezones: string[], unit: unitOfTime.Base): boolean {
    return timezones.every(timezone => {
      const localTs = moment.tz(ts, timezone);
      return this.isLocalTsAlignedWithBoundary(localTs, unit);
    });
  }

  private getSiteTimezones(searchCriteria: MeterDataSearchCriteria): Observable<string[] | null> {
    const siteIds = new Set(searchCriteria.siteIds);
    if (searchCriteria.siteId != null) {
      siteIds.add(searchCriteria.siteId);
    }
    return siteIds.size
      ? this.siteService.getCachedSiteInfos(Array.from(siteIds)).pipe(map(sites => sites.map(s => s.timezone)))
      : of(null);
  }

  // callers of this method should bear the responsibility of splitting request into (re)active_net and other meter data types
  // as rollup data is only available for (re)active_net.
  private getMeterDataRollupType(searchCriteria: MeterDataSearchCriteria): Observable<MeterDataRollupType | null> {
    const canUseRollup = searchCriteria.preferRollup
      && this.METER_DATA_TYPES_WITH_ROLLUP.has(searchCriteria.dataType)
      && !searchCriteria.resultTypes?.includes(MeterDataResultType.EMISSIONS);
    if (canUseRollup) {
      const availableRollupTypes = this.ROLLUP_TYPES_BY_AGGREGATE_PERIOD.get(searchCriteria.aggregatePeriod);
      if (availableRollupTypes?.length) {
        const {localStartTs, localEndTs, startTs, endTs} = searchCriteria;
        const timezones$: Observable<string[] | null> = (startTs || endTs)
          ? this.getSiteTimezones(searchCriteria)
          : of(null);
        return timezones$.pipe(map(timezones =>
          availableRollupTypes.find(rollupType => {
            const unit: unitOfTime.Base = rollupType === MeterDataRollupType.MONTHLY
              ? 'month'
              : (rollupType === MeterDataRollupType.DAILY
                ? 'day'
                : null);
            if (unit) {
              return (!localStartTs || this.isLocalTsAlignedWithBoundary(localStartTs, unit))
                && (!localEndTs || this.isLocalTsAlignedWithBoundary(localEndTs, unit))
                && (!startTs || this.isTsAlignedWithBoundary(startTs, timezones, unit))
                && (!endTs || this.isTsAlignedWithBoundary(endTs, timezones, unit));
            } else {
              return false;
            }
          }) ?? null));
      }
    }
    return of(null);
  }

  public getMeterDataFetchPageSize(searchCriteria: MeterDataSearchCriteria, streamCount: number, pageStepUnit: DurationTuple): Observable<number> {
    return this.getMeterDataRollupType(searchCriteria).pipe(map(rollupType => {
      const chunkSize = this.calculateMeterDataFetchChunkSize(streamCount, pageStepUnit);
      const rollupFactor = rollupType === MeterDataRollupType.DAILY
        ? this.DALIY_ROLLUP_MULTIPLY_FACTOR
        : (rollupType === MeterDataRollupType.MONTHLY
          ? this.MONTHLY_ROLLUP_MULTIPLY_FACTOR
          : 1);
      return chunkSize * rollupFactor;
    }));
  }
}
